import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// ─── Locale resources (auto-loaded) ───
// All translation catalogs are loaded from src/i18n/locales/<lang>/<namespace>.json
// via Vite's import.meta.glob. This replaces ~880 lines of hand-written static
// imports + a manually-maintained `resources` map. Any locale file present on
// disk is registered automatically, so adding a language or namespace no longer
// requires editing this file (this was the root cause of the AppSumo namespace
// being missing for 12 languages).
//
// `eager: true` keeps the catalogs bundled (same behaviour as the previous
// static imports — no lazy loading / network fetch at runtime).
const localeModules = import.meta.glob('./locales/*/*.json', { eager: true });

const resources = {};
for (const filePath in localeModules) {
  // filePath looks like './locales/en/common.json'
  const match = filePath.match(/\.\/locales\/(.+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, lang, namespace] = match;
  const mod = localeModules[filePath];
  if (!resources[lang]) resources[lang] = {};
  resources[lang][namespace] = mod.default ?? mod;
}

// ─── Supported UI languages ───
// To add a new language:
// 1. Create a new folder in locales/ (e.g., locales/fr/) with the namespace JSON
//    files (run `npm run i18n:extract` to scaffold every key from the code).
// 2. Translate the values (or run `npm run i18n:translate`).
// 3. Add the language to `supportedUILanguages` below.
// Registration in `resources` is automatic via the glob loader above.
export const supportedUILanguages = [
  { code: 'en', flag: 'gb', name: 'English' },
  { code: 'es', flag: 'es', name: 'Español' },
  { code: 'fr', flag: 'fr', name: 'Français' },
  { code: 'pt', flag: 'br', name: 'Português' },
  { code: 'de', flag: 'de', name: 'Deutsch' },
  { code: 'it', flag: 'it', name: 'Italiano' },
  { code: 'nl', flag: 'nl', name: 'Nederlands' },
  { code: 'ja', flag: 'jp', name: '日本語' },
  { code: 'tr', flag: 'tr', name: 'Türkçe' },
  { code: 'hi', flag: 'in', name: 'हिन्दी' },
  { code: 'ta', flag: 'in', name: 'தமிழ்' },
  { code: 'bn', flag: 'bd', name: 'বাংলা' },
  { code: 'zh-CN', flag: 'cn', name: '简体中文' },
  { code: 'zh-TW', flag: 'tw', name: '繁體中文' },
];

/**
 * Helper to render a flag image for a language.
 * Uses flagcdn.com SVG flags which work on all platforms (unlike emoji flags on Windows).
 */
export const getFlagUrl = (countryCode) =>
  `https://flagcdn.com/w20/${countryCode}.png`;

// Detect initial language: check localStorage preference, then browser language.
function getInitialLanguage() {
  // 1. Check localStorage for saved interface language preference
  try {
    const stored = localStorage.getItem('aerochat_interface_lang');
    if (stored && resources[stored]) return stored;
  } catch { /* ignore */ }

  // 2. Try browser language (full tag first for zh-CN / zh-TW, then base)
  const browserLang = navigator.language;
  if (browserLang) {
    if (resources[browserLang]) return browserLang;
    const baseLang = browserLang.split('-')[0];
    if (resources[baseLang]) return baseLang;
  }

  // 3. Default to English
  return 'en';
}

// Dev-only visibility: when a key resolves via the English fallback (or is
// empty), log it so we can see exactly which `namespace:key` is untranslated
// while clicking through the UI. No-op in production builds.
const isDev = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: Object.keys(resources.en),
    // Treat an empty-string value as "missing" so it falls back to English
    // instead of rendering blank. This lets the i18n tooling stub untranslated
    // keys as "" (an explicit "needs translation" signal) without ever showing
    // an empty label to the user.
    returnEmptyString: false,
    // Dev-only: surface fallbacks/missing keys in the console.
    saveMissing: !!isDev,
    missingKeyHandler: (lngs, ns, key) => {
      if (isDev) {
        console.warn('[i18n] missing/untranslated:', `${ns}:${key}`, '→ langs:', lngs);
      }
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
  });

// Set HTML lang attribute on language change
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  // Persist to localStorage for pre-login fallback
  try {
    localStorage.setItem('aerochat_interface_lang', lng);
  } catch { /* ignore */ }
});

// Set initial HTML lang
document.documentElement.lang = i18n.language;

export default i18n;
